/**
 * Pure builders for the WLS search request and the map camera.
 *
 * These are intentionally free of any network/React dependency so they can be
 * unit tested in isolation (see spec/javascript/wls-search-query.spec.js).
 *
 * IMPORTANT — geo bounds contract:
 *   `searchState.geo{NE,SW}{Lat,Lon}` are the *explicit* search bounds — set
 *   only from an explicit user action (a shared/deep-linked URL or the map
 *   "Search This Area" control). A search *response* never writes those query
 *   fields; its result-fitted bounds are surfaced through
 *   `mapCameraFieldsFromResponse` as separate `map*` fields that only drive the
 *   map camera. This keeps result-fitted bounds from being re-sent on the next
 *   search and progressively narrowing the catalog (the "filters won't clear /
 *   listings don't come back" bug).
 */

const serializeAmenitiesParam = require('./wls-search-amenities');

const formatDate = value => {
  if (!value) return null;
  if (typeof value.format === 'function') return value.format('YYYY-MM-DD');
  return String(value);
};

const buildBookingRangeParam = searchState => {
  if (
    Array.isArray(searchState.bookingRange) &&
    searchState.bookingRange.length
  ) {
    return JSON.stringify(searchState.bookingRange);
  }
  const checkInDate = formatDate(searchState.checkIn || searchState.checkInDate);
  const checkOutDate = formatDate(
    searchState.checkOut || searchState.checkOutDate
  );
  if (!checkInDate || !checkOutDate) return undefined;
  // eslint-disable-next-line global-require
  const moment = require('moment');
  const range = [];
  const day = moment(checkInDate, 'YYYY-MM-DD');
  const end = moment(checkOutDate, 'YYYY-MM-DD');
  while (day.isSameOrBefore(end, 'day')) {
    range.push({ key: day.format('DD-MM-YYYY'), day: day.day() });
    day.add(1, 'day');
  }
  return range.length ? JSON.stringify(range) : undefined;
};

const buildWlsSearchParams = (searchState, brand) => {
  return {
    organization_id: brand.organization.id,
    brand_id: brand.id,
    page: (searchState.currentPage || 0) + 1,
    limit: 18,
    active: 'true',
    booking_range: buildBookingRangeParam(searchState),
    num_guests: searchState.guests || undefined,
    num_bedrooms: searchState.filter_numBedrooms || undefined,
    num_bathrooms: searchState.filter_numBathrooms || undefined,
    instant_book: Boolean(searchState.filter_instantBook),
    price_low: searchState.filter_priceLow || undefined,
    price_high: searchState.filter_priceHigh || undefined,
    geo_ne_lat: searchState.geoNELat || undefined,
    geo_ne_lon: searchState.geoNELon || undefined,
    geo_sw_lat: searchState.geoSWLat || undefined,
    geo_sw_lon: searchState.geoSWLon || undefined,
    loc: searchState.loc || undefined,
    distance: searchState.filter_distance || undefined,
    amenities: serializeAmenitiesParam(searchState.amenities),
    event_search: searchState.eventSearch || undefined,
    sort:
      searchState.sort && searchState.sort !== 'default'
        ? searchState.sort
        : undefined
  };
};

const mapCameraFieldsFromResponse = data => ({
  mapBoundsNELat: data && data.bounds ? data.bounds.ne.lat : null,
  mapBoundsNELon: data && data.bounds ? data.bounds.ne.lng : null,
  mapBoundsSWLat: data && data.bounds ? data.bounds.sw.lat : null,
  mapBoundsSWLon: data && data.bounds ? data.bounds.sw.lng : null,
  mapCenterLat: data && data.center ? data.center[0] : null,
  mapCenterLon: data && data.center ? data.center[1] : null
});

/**
 * Price-slider bounds contract:
 *   A search response's `min_price`/`max_price` are aggregated over the
 *   *filtered* result set, so as soon as a price filter is active they collapse
 *   to the filtered range. The price slider debounces a search on every drag, so
 *   feeding those shrinking bounds back into the slider's min/max scale mid-drag
 *   makes the handle jump and desync from its displayed value (the reported
 *   "price scrollbar jumps / hard to use" bug). We therefore only refresh the
 *   slider bounds from responses where the user has NOT set an explicit price
 *   filter; while a price filter is active the catalog bounds stay fixed.
 */
const priceFilterActive = searchState =>
  Boolean(
    searchState &&
      ((searchState.filter_priceLow !== null &&
        searchState.filter_priceLow !== undefined) ||
        (searchState.filter_priceHigh !== null &&
          searchState.filter_priceHigh !== undefined))
  );

const mapPriceBoundsFromResponse = (data, searchState) => {
  if (priceFilterActive(searchState)) return {};
  return {
    min_price: (data && data.min_price) || 0,
    max_price: (data && data.max_price) || null
  };
};

module.exports = buildWlsSearchParams;
module.exports.buildWlsSearchParams = buildWlsSearchParams;
module.exports.buildBookingRangeParam = buildBookingRangeParam;
module.exports.mapCameraFieldsFromResponse = mapCameraFieldsFromResponse;
module.exports.mapPriceBoundsFromResponse = mapPriceBoundsFromResponse;
